import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react"

export function createErpContext<Store>(initialState: Store) {
  const useStoreData = (): {
    get: () => Store
    set: (
      value: Partial<Store> | ((prevState: Store) => Partial<Store>)
    ) => void
    subscribe: (callback: () => void) => () => void
  } => {
    const store = useRef(initialState)
    const get = useCallback(() => store.current, [])
    const subscribers = useRef(new Set<() => void>())
    const set = useCallback(
      (value: Partial<Store> | ((prevState: Store) => Partial<Store>)) => {
        const currentState = store.current
        const newState =
          typeof value === "function" ? value(currentState) : value
        store.current = { ...currentState, ...newState }
        subscribers.current.forEach((callback) => callback())
      },
      []
    )
    const subscribe = useCallback((callback: () => void) => {
      subscribers.current.add(callback)
      return () => subscribers.current.delete(callback)
    }, [])

    return {
      get,
      set,
      subscribe,
    }
  }

  type UseStoreDataReturnType = ReturnType<typeof useStoreData>

  const StoreContext = createContext<UseStoreDataReturnType | null>(null)
  const ContextProvider = ({ children }: { children: React.ReactNode }) => {
    return (
      <StoreContext.Provider value={useStoreData()}>
        {children}
      </StoreContext.Provider>
    )
  }

  function useGlobalContext(): [
    Store,
    (value: Partial<Store> | ((prevState: Store) => Partial<Store>)) => void
  ] {
    const store = useContext(StoreContext)
    if (!store) {
      throw new Error("Store not found")
    }
    // TODO: Performance. try to use useReducer
    // function reducer(state: Store, action: Partial<Store>): Store {
    //   return { ...state, ...action }
    // }
    // const [state, dispatch] = useReducer(reducer, store.get())
    const [state, setState] = useState(store.get())

    useEffect(() => {
      return store.subscribe(() => {
        // TODO: Performance. try to use useReducer
        // dispatch(store.get())
        setState(store.get())
      })
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return [state, store.set]
  }

  return {
    ContextProvider,
    useGlobalContext,
  }
}
